import { Stack } from "@mui/material";
import SignUpForm from "./components/SignUpForm";
import { useCallback, useEffect, useMemo } from "react";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useCreateMeMutation } from "src/api/services/patientApi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCreateVisitMutation } from "src/api/services/visitApi";
import AllRightsReserved from "src/components/AllRightsReserved";
import { usePreferredLanguage } from "src/context/PreferredLanguageProvider";
import { useProduct } from "src/context/ProductProvider";
import { isValidLanguage } from "src/utils/isValidLanguage";
import { Paths } from "src/constants/paths";
import { useSentryLogger } from "src/hooks/useSentryLogger";

const Signup = () => {
  const isMobile = useIsMobile();
  const [createVisit] = useCreateVisitMutation();
  const [createMe] = useCreateMeMutation();
  const { variant } = useParams<{ variant?: string | undefined }>();
  const logger = useSentryLogger();
  const navigate = useNavigate();
  const language = usePreferredLanguage();
  const [searchParams] = useSearchParams();

  const referralCode = useMemo(
    () => searchParams.get("referral_code"),
    [searchParams]
  );

  const productSlug = useProduct((p) => {
    if (["ec", "on"].includes(p.slug)) return "st";

    return p.slug;
  });

  const onSuccess = useCallback(
    async (profileId: string) => {
      sessionStorage.setItem("justSignedUp", "true");

      try {
        await createMe({
          profile_id: profileId,
          referred_by_code: referralCode ?? undefined,
          profile: {
            language: isValidLanguage(language) ? language : undefined,
          },
          metadata: {
            fp_device_id: window.freshpaint?.cookie?.props?.$device_id,
          },
        }).unwrap();

        await createVisit({
          product: productSlug,
          titration: variant,
        }).unwrap();

        navigate(Paths.AUTH_QUEUE);
      } catch (err) {
        logger("onSuccess callback failed", err);
      }
    },
    [
      createMe,
      referralCode,
      language,
      createVisit,
      productSlug,
      variant,
      navigate,
      logger,
    ]
  );

  useEffect(() => {
    if (isMobile) {
      document.documentElement.classList.add("signup-page");

      return () => {
        document.documentElement.classList.remove("signup-page");
      };
    }
  }, [isMobile]);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      padding="1rem"
    >
      <SignUpForm
        isSignUp={true}
        onSuccess={onSuccess}
        productSlug={productSlug}
        referralCode={referralCode}
        variant={variant}
      />
      <AllRightsReserved />
    </Stack>
  );
};

export default Signup;
